import React, { useMemo } from "react";
import { Avatar, Icon } from "@medentic/polaris-react";
import { HealthcareCompany, HealthcareCompanyType } from "@shared/types/commonTypes";
import { CheckSmallIcon } from "@medentic/polaris-icons";
import { ROOTS } from "@app/config/constants";
import { setLocalStorage } from "@shared/lib";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useGetSingleCompanyDetails } from "@shared/hooks/common/useGetSingleCompanyDetails";
import { MedenticTopBar, MenuAction } from "@shared/ui";

type ClinicTopBarProps = {
  onNavigationToggle: () => void;
};

const EMPTY_CLINICS: HealthcareCompany[] = [];

const ClinicTopBar: React.FC<ClinicTopBarProps> = ({ onNavigationToggle }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const clinicsData = queryClient.getQueryData<HealthcareCompany[]>([
    "healthcare-companies",
    HealthcareCompanyType.DENTAL_CLINIC,
  ]);
  const clinics = useMemo(
    () => clinicsData ?? EMPTY_CLINICS,
    [clinicsData]
  );

  const params = useParams();
  // Exclude "add" from being treated as a valid clinic ID
  const selectedClinicId = params.clinicId !== "add" ? params.clinicId : undefined;

  // Get detailed company information using the hook
  const { company } = useGetSingleCompanyDetails(selectedClinicId, {
    refetchOnMount: false, // Use cached data for performance
    staleTime: 1000 * 60 * 10, // Cache for 10 minutes
  });

  const selectedClinic =
    clinics.find((c) => c.id === selectedClinicId) || clinics[0];

  // Prepare organization menu items
  const organizationMenuItems: MenuAction[] = useMemo(() => {
    // Filter by the actual selected clinic's ID to prevent duplicates
    const sortedClinics = clinics
      .filter((clinic) => clinic.id !== selectedClinic?.id)
      .sort((a, b) => a.name.localeCompare(b.name));

    const clinicsToList = [selectedClinic, ...sortedClinics].filter((c) => !!c);


    return clinicsToList.slice(0, 4).map((c) => {
      // Create a stable reference for the icon function
      const IconComponent = () => (
        <Avatar
          initials={c.avatar.initials}
          name={c.name}
          source={c.avatar.image}
        />
      );
      
      return {
        content: c.name,
        icon: IconComponent,
        onAction: () => {
          // User explicitly selected this clinic, navigate directly to it
          setLocalStorage("__LAST_VISITED_C_ID", c.id);
          navigate(`${ROOTS.CLINIC}/${c.id}`);
        },
        active: c.id === selectedClinicId,
        suffix: c.id === selectedClinicId && <Icon source={CheckSmallIcon} />,
      };
    });
  }, [clinics, selectedClinic, selectedClinicId, navigate]);

  return (
    <MedenticTopBar
      onNavigationToggle={onNavigationToggle}
      userMenuName={company?.name ?? selectedClinic?.name ?? ""}
      userMenuInitials={company?.avatar?.initials ?? selectedClinic?.avatar.initials}
      userMenuAvatar={company?.avatar?.image ?? selectedClinic?.avatar.image}
      organizationMenuItems={organizationMenuItems}
      allOrganizationsLabel={t("topbar.clinics")}
      allOrganizationsRoute={ROOTS.CLINIC}
      showSearchField={true}
      showLocaleSwitcher={true}
      showPIIToggle={true}
      showUserProfileInMenu={true}
    />
  );
};

export default React.memo(ClinicTopBar);
